(() => {
  'use strict';

  const STORAGE_KEY = 'roadnestVehicleCheckerV3';
  const CURRENT_YEAR = new Date().getFullYear();
  const EARLIEST_YEAR = 1950;
  const BRAND_ALIASES = {
    vw: 'Volkswagen',
    volkswagen: 'Volkswagen',
    citroen: 'Citroën',
    skoda: 'Škoda',
    seat: 'SEAT',
    mercedes: 'Mercedes-Benz',
    'mercedes benz': 'Mercedes-Benz',
    landrover: 'Land Rover',
    'land rover': 'Land Rover',
    alfa: 'Alfa Romeo',
    'alfa romeo': 'Alfa Romeo'
  };
  const PLACEHOLDER_MODELS = {
    peugeot: '3008',
    seat: 'Ibiza',
    renault: 'Captur',
    volkswagen: 'Tiguan',
    volvo: 'XC60',
    bmw: 'X3',
    audi: 'Q5',
    dacia: 'Duster'
  };

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const escapeHTML = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatEuroPrice = (value) => {
    const amount = Number(String(value || '').replace(/[^0-9]/g, ''));
    return amount ? `${amount.toLocaleString('fr-FR')} €` : String(value || '');
  };

  const track = (name, data = {}) => {
    if (window.Roadnest && typeof window.Roadnest.track === 'function') {
      window.Roadnest.track(name, data);
    }
  };

  document.querySelectorAll('[data-vehicle-checker]').forEach((checker) => {
    const form = checker.querySelector('[data-vehicle-form]');
    const steps = [...checker.querySelectorAll('[data-step]')];
    const progressSteps = [...checker.querySelectorAll('[data-progress-step]')];
    const progressBar = checker.querySelector('[data-progress-bar]');
    const backButton = checker.querySelector('[data-vehicle-back]');
    const nextButton = checker.querySelector('[data-vehicle-next]');
    const navigation = checker.querySelector('[data-wizard-nav]');
    const errorBox = checker.querySelector('[data-vehicle-error]');
    const brandList = checker.querySelector('[data-brand-list]');
    const modelList = checker.querySelector('[data-model-list]');
    const brandHint = checker.querySelector('[data-brand-hint]');
    const modelHint = checker.querySelector('[data-model-hint]');
    const modelSelectField = checker.querySelector('[data-model-select-field]');
    const manualModelField = checker.querySelector('[data-manual-model-field]');
    const manualModelToggle = checker.querySelector('[data-manual-model-toggle]');
    const modelListToggle = checker.querySelector('[data-model-list-toggle]');
    const decisionScreen = checker.querySelector('[data-decision-screen]');
    const decisionTitle = checker.querySelector('[data-decision-title]');
    const decisionHelp = checker.querySelector('[data-decision-help]');
    const decisionOptions = checker.querySelector('[data-decision-options]');
    const resultTarget = checker.querySelector('[data-vehicle-result]');

    let catalogue = [];
    let proofs = [];
    let dataReady = false;
    let started = false;
    let finalMode = '';
    let resultTracked = false;

    const defaults = {
      step: 0,
      product: '',
      brand: '',
      model: '',
      manualModel: '',
      modelMode: 'catalogue',
      year: '',
      lookupKey: '',
      bodyType: '',
      hatch: '',
      result: '',
      reason: ''
    };

    const stored = (() => {
      try {
        const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (_error) {
        return {};
      }
    })();
    const state = { ...defaults, ...stored };
    state.step = Math.min(4, Math.max(0, Number(state.step) || 0));

    const requestedProduct = new URLSearchParams(window.location.search).get('product');
    if (['summit', 'nomad'].includes(requestedProduct)) {
      if (state.product !== requestedProduct) state.step = 0;
      state.product = requestedProduct;
    }

    const save = () => {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_error) {}
    };

    const optionMarkup = (values) => values.map((value) => `<option value="${escapeHTML(value)}"></option>`).join('');
    const brands = () => catalogue.map((entry) => entry.brand);

    const canonicalBrandEntry = (value) => {
      const normalized = normalize(value);
      const alias = BRAND_ALIASES[normalized];
      return catalogue.find((entry) => normalize(entry.brand) === normalize(alias || value)) || null;
    };

    const modelsForBrand = (brandValue = state.brand) => canonicalBrandEntry(brandValue)?.models || [];

    const canonicalModelEntry = (value, brandValue = state.brand) => {
      const normalized = normalize(value);
      return modelsForBrand(brandValue).find((model) => (
        normalize(model.name) === normalized ||
        (model.aliases || []).some((alias) => normalize(alias) === normalized)
      )) || null;
    };

    const selectedModelEntry = () => (
      state.modelMode === 'manual' ? null : canonicalModelEntry(state.model, state.brand)
    );

    const populateBrands = () => {
      brandList.innerHTML = optionMarkup(brands());
      brandHint.textContent = dataReady
        ? `${brands().length} marques disponibles`
        : 'Chargement du catalogue…';
    };

    const populateModels = () => {
      const models = modelsForBrand();
      modelList.innerHTML = optionMarkup(models.map((model) => model.name));
      const brandEntry = canonicalBrandEntry(state.brand);
      const modelInput = form.elements.model;
      if (!brandEntry) {
        modelInput.placeholder = 'Rechercher un modèle';
        modelHint.textContent = 'Sélectionnez d’abord une marque.';
        return;
      }
      const preferred = PLACEHOLDER_MODELS[normalize(brandEntry.brand)];
      const example = models.find((model) => normalize(model.name) === normalize(preferred)) || models[0];
      modelInput.placeholder = example ? `Ex. ${example.name}` : 'Rechercher un modèle';
      modelHint.textContent = `${models.length} modèle${models.length > 1 ? 's' : ''} · triés par popularité puis par ordre alphabétique`;
    };

    const populateYears = () => {
      const years = [];
      for (let year = CURRENT_YEAR; year >= EARLIEST_YEAR; year -= 1) years.push(year);
      form.elements.year.innerHTML = '<option value="">Choisir une année</option>' +
        years.map((year) => `<option value="${year}">${year}</option>`).join('');
      if (years.includes(Number(state.year))) form.elements.year.value = state.year;
    };

    const syncModelMode = () => {
      const manual = state.modelMode === 'manual';
      modelSelectField.hidden = manual;
      manualModelField.hidden = !manual;
      if (manual) {
        form.elements.manual_model.value = state.manualModel || '';
      } else {
        form.elements.model.value = state.model || '';
      }
    };

    const syncForm = () => {
      if (form.elements.product && state.product) form.elements.product.value = state.product;
      form.elements.brand.value = state.brand || '';
      populateBrands();
      populateModels();
      populateYears();
      syncModelMode();
    };

    const clearError = () => {
      errorBox.hidden = true;
      errorBox.textContent = '';
    };

    const showError = (message) => {
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.focus();
    };

    const vehicleName = (includeYear = true) => {
      const base = `${state.brand} ${state.model}`.trim();
      return includeYear && state.year ? `${base} ${state.year}` : base;
    };

    const productURL = (product, result) => {
      const base = product === 'summit' ? checker.dataset.summitUrl : checker.dataset.nomadUrl;
      const url = new URL(base, window.location.origin);
      url.searchParams.set('vehicle', `${state.brand} ${state.model}`.trim());
      url.searchParams.set('year', state.year);
      url.searchParams.set('compatibility', result);
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const manualButton = (product, result, reason, hatch = '') => `
      <button
        type="button"
        class="btn"
        data-open-compatibility
        data-compat-product="${escapeHTML(product)}"
        data-compat-brand="${escapeHTML(state.brand)}"
        data-compat-model="${escapeHTML(state.model)}"
        data-compat-year="${escapeHTML(state.year)}"
        data-compat-body="${escapeHTML(state.bodyType)}"
        data-compat-result="${escapeHTML(result)}"
        data-compat-reason="${escapeHTML(reason)}"
        data-compat-hatch="${escapeHTML(hatch || state.hatch)}"
      >Faire vérifier gratuitement</button>`;

    const productCard = (product, result) => {
      const summit = product === 'summit';
      const title = summit ? 'ROADNEST Summit' : 'ROADNEST Nomad';
      const description = summit
        ? 'Tente de toit familiale avec auvent intégré'
        : 'Tente de hayon spacieuse qui repose au sol';
      const price = formatEuroPrice(summit ? checker.dataset.summitPrice : checker.dataset.nomadPrice);
      const url = productURL(product, result);
      return `
        <aside class="vehicle-result__product">
          <div><small>${title}</small><strong>${escapeHTML(description)}</strong><span>${escapeHTML(price)}</span></div>
          <a class="btn" data-product-link="${product}" href="${url}">Découvrir ${title}</a>
        </aside>`;
    };

    const resultShell = ({ type, status, heading, intro, checks = [], content = '', actions = '', note = '' }) => {
      const mark = type === 'compatible' ? '✓' : (type === 'incompatible' ? '×' : 'i');
      return `
        <article class="vehicle-result vehicle-result--${type}">
          <span class="vehicle-result__mark" aria-hidden="true">${mark}</span>
          <p class="vehicle-result__status">${escapeHTML(status)}</p>
          <h2>${escapeHTML(heading)}</h2>
          <p class="vehicle-result__intro">${escapeHTML(intro)}</p>
          ${checks.length ? `<ul class="vehicle-result__checks">${checks.map((check) => `<li>${escapeHTML(check)}</li>`).join('')}</ul>` : ''}
          ${content}
          ${actions ? `<div class="vehicle-result__actions">${actions}</div>` : ''}
          ${note ? `<p class="vehicle-result__assistance">${escapeHTML(note)}</p>` : ''}
        </article>`;
    };

    const proofsForSelection = () => {
      if (state.modelMode === 'manual' || !state.lookupKey) return [];
      const year = Number(state.year);
      return proofs.filter((proof) => {
        if (proof.compatibility_lookup_key !== state.lookupKey) return false;
        if (!year || !proof.year_from) return false;
        const end = proof.year_to || Number.POSITIVE_INFINITY;
        return year >= proof.year_from && year <= end;
      });
    };

    const verificationOutcome = (product, reason) => {
      state.result = 'verification';
      state.reason = reason;
      return {
        result: 'verification',
        html: resultShell({
          type: 'verification',
          status: 'VÉRIFICATION GRATUITE',
          heading: 'Nous devons confirmer un dernier détail.',
          intro: 'Votre véhicule est bien enregistré. Notre assistance vérifie gratuitement la charge autorisée pour cette génération avant de confirmer l’installation.',
          checks: [`Véhicule enregistré : ${vehicleName()}`, 'Demande préremplie', 'Réponse personnalisée par notre assistance française'],
          actions: `${manualButton(product, 'Vérification nécessaire', reason)}<button type="button" class="link-underline" data-review-vehicle>Modifier mon véhicule</button>`,
          note: 'La demande ne vous engage à aucun achat.'
        })
      };
    };

    const evaluateSummit = () => {
      const matches = proofsForSelection();
      if (!matches.length) {
        return verificationOutcome('summit', state.modelMode === 'manual' ? 'Modèle saisi manuellement' : 'Preuve à confirmer pour cette année');
      }
      if (matches.some((proof) => proof.data_status !== 'Validé' || !Number.isFinite(Number(proof.dynamic_roof_load_kg)))) {
        return verificationOutcome('summit', 'Preuve provisoire, contradictoire ou insuffisamment précise');
      }

      const restrictiveLoad = Math.min(...matches.map((proof) => Number(proof.dynamic_roof_load_kg)));
      if (restrictiveLoad >= 65) {
        state.result = 'compatible';
        state.reason = 'Preuve validée et charge dynamique suffisante';
        return {
          result: 'compatible',
          html: resultShell({
            type: 'compatible',
            status: 'COMPATIBILITÉ CONFIRMÉE',
            heading: 'Bonne nouvelle, votre véhicule est compatible.',
            intro: 'La charge dynamique autorisée pour cette configuration permet l’installation de ROADNEST Summit avec un système de barres adapté au véhicule.',
            checks: ['Preuve constructeur validée', 'Seuil ROADNEST Summit respecté', 'Système de barres adapté à sélectionner'],
            content: productCard('summit', 'compatible')
          })
        };
      }

      state.result = 'incompatible';
      state.reason = 'Charge dynamique validée inférieure à 65 kg';
      return {
        result: 'incompatible',
        html: resultShell({
          type: 'incompatible',
          status: 'CONFIGURATION NON ADAPTÉE',
          heading: 'ROADNEST Summit n’est pas compatible avec cette configuration.',
          intro: 'La charge dynamique validée pour cette configuration ne permet pas l’installation du système Summit.',
          checks: [`Configuration vérifiée : ${vehicleName()}`, 'Installation Summit non recommandée'],
          content: `<p class="vehicle-result__alternative">Votre véhicule peut cependant être compatible avec notre tente de hayon ROADNEST Nomad, qui repose au sol et ne charge pas le toit.</p>${productCard('nomad', 'alternative')}`
        })
      };
    };

    const evaluateNomad = () => {
      if (state.modelMode === 'manual' || state.hatch === 'unknown') {
        state.result = 'verification';
        state.reason = state.modelMode === 'manual' ? 'Modèle saisi manuellement' : 'Type de hayon incertain';
        return {
          result: 'verification',
          html: resultShell({
            type: 'verification',
            status: 'VÉRIFICATION GRATUITE',
            heading: 'Nous devons confirmer un dernier détail.',
            intro: 'Votre véhicule est bien enregistré. Notre assistance vérifie gratuitement la forme et le sens d’ouverture du hayon.',
            checks: [`Véhicule enregistré : ${vehicleName()}`, 'Demande préremplie', 'Une photo du hayon peut être ajoutée'],
            actions: `${manualButton('nomad', 'Vérification nécessaire', state.reason, state.hatch)}<button type="button" class="link-underline" data-review-vehicle>Modifier mon véhicule</button>`,
            note: 'Réponse personnalisée par notre assistance française.'
          })
        };
      }
      if (state.hatch === 'yes') {
        state.result = 'compatible';
        state.reason = 'Hayon arrière relevable confirmé';
        return {
          result: 'compatible',
          html: resultShell({
            type: 'compatible',
            status: 'COMPATIBILITÉ HAYON CONFIRMÉE',
            heading: 'Bonne nouvelle, votre véhicule est compatible avec ROADNEST Nomad.',
            intro: 'Votre hayon s’ouvre vers le haut : la Nomad peut se raccorder à l’arrière du véhicule tout en reposant au sol.',
            checks: ['Hayon arrière relevable confirmé', 'Installation au sol', 'Accès direct au coffre'],
            content: productCard('nomad', 'compatible')
          })
        };
      }
      state.result = 'incompatible';
      state.reason = 'Absence de hayon arrière relevable';
      return {
        result: 'incompatible',
        html: resultShell({
          type: 'incompatible',
          status: 'HAYON NON ADAPTÉ',
          heading: 'ROADNEST Nomad n’est pas compatible avec cette ouverture arrière.',
          intro: 'La Nomad doit se raccorder autour d’un hayon conventionnel qui s’ouvre vers le haut.',
          checks: [`Véhicule sélectionné : ${vehicleName()}`, 'Ouverture arrière non adaptée à la liaison Nomad'],
          actions: `${manualButton('nomad', 'Configuration non adaptée', state.reason, state.hatch)}`
        })
      };
    };

    const bindResultActions = (result) => {
      resultTarget.querySelector('[data-review-vehicle]')?.addEventListener('click', () => {
        state.step = 1;
        state.hatch = '';
        state.result = '';
        state.reason = '';
        finalMode = '';
        resultTracked = false;
        save();
        render();
      });
      resultTarget.querySelectorAll('[data-product-link]').forEach((link) => {
        link.addEventListener('click', () => track('compatibility_product_click', {
          selected_product: state.product,
          destination_product: link.dataset.productLink,
          result
        }));
      });
    };

    const showResult = () => {
      const outcome = state.product === 'nomad' ? evaluateNomad() : evaluateSummit();
      resultTarget.innerHTML = outcome.html;
      resultTarget.hidden = false;
      decisionScreen.hidden = true;
      finalMode = 'result';
      bindResultActions(outcome.result);
      save();
      if (!resultTracked) {
        track('compatibility_result', {
          product: state.product,
          result: outcome.result,
          brand: state.brand,
          model: state.model,
          year: state.year,
          reason: state.reason
        });
        resultTracked = true;
      }
    };

    const prepareHatchDecision = () => {
      finalMode = 'decision';
      resultTarget.hidden = true;
      decisionScreen.hidden = false;
      decisionTitle.textContent = 'Votre coffre possède-t-il un hayon qui s’ouvre vers le haut ?';
      decisionHelp.textContent = 'Le panneau arrière et la lunette se lèvent ensemble au-dessus du coffre.';
      decisionOptions.innerHTML = [
        ['yes', 'Oui', 'Le hayon complet se relève vers le haut'],
        ['no', 'Non', 'Coffre séparé, portes battantes ou autre ouverture'],
        ['unknown', 'Je ne suis pas sûr', 'Notre assistance peut le vérifier gratuitement']
      ].map(([value, label, help]) => `<label class="vehicle-option"><input type="radio" name="hatch" value="${value}"${state.hatch === value ? ' checked' : ''}><span><strong>${label}</strong><small>${help}</small></span></label>`).join('');
    };

    const prepareFinal = () => {
      state.step = 4;
      resultTracked = false;
      if (state.product === 'nomad') prepareHatchDecision();
      else showResult();
    };

    const validateStep = () => {
      clearError();
      if (state.step === 0 && !state.product) {
        showError('Choisissez ROADNEST Summit ou ROADNEST Nomad.');
        return false;
      }
      if (state.step === 1) {
        const brandEntry = canonicalBrandEntry(form.elements.brand.value);
        if (!brandEntry) {
          showError('Choisissez une marque proposée dans le catalogue.');
          return false;
        }
        state.brand = brandEntry.brand;
        form.elements.brand.value = brandEntry.brand;
      }
      if (state.step === 2) {
        if (state.modelMode === 'manual') {
          const manualValue = form.elements.manual_model.value.trim();
          if (!manualValue) {
            showError('Saisissez le modèle de votre véhicule.');
            return false;
          }
          state.manualModel = manualValue;
          state.model = manualValue;
          state.lookupKey = '';
          state.bodyType = '';
        } else {
          const modelEntry = canonicalModelEntry(form.elements.model.value);
          if (!modelEntry) {
            showError('Choisissez un modèle proposé ou utilisez la saisie manuelle.');
            return false;
          }
          state.model = modelEntry.name;
          state.lookupKey = modelEntry.lookup_key;
          state.bodyType = modelEntry.body_type || '';
          form.elements.model.value = modelEntry.name;
        }
      }
      if (state.step === 3) {
        if (!form.elements.year.value) {
          showError('Choisissez l’année de votre véhicule.');
          return false;
        }
        state.year = form.elements.year.value;
      }
      if (state.step === 4 && finalMode === 'decision') {
        const hatch = form.elements.hatch?.value;
        if (!hatch) {
          showError('Indiquez le type d’ouverture arrière.');
          return false;
        }
        state.hatch = hatch;
      }
      save();
      return true;
    };

    const focusCurrentStep = () => {
      const heading = steps[state.step]?.querySelector('legend, h2');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      window.setTimeout(() => heading.focus({ preventScroll: true }), 25);
    };

    const render = (focus = true) => {
      steps.forEach((step, index) => { step.hidden = index !== state.step; });
      progressSteps.forEach((step, index) => {
        if (index === state.step) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
      const progress = `${(state.step + 1) * 20}%`;
      progressBar.parentElement.style.setProperty('--progress', progress);
      backButton.hidden = state.step === 0 || (state.step === 4 && finalMode === 'result');
      navigation.hidden = state.step === 4 && finalMode === 'result';
      nextButton.innerHTML = state.step === 4 ? 'Voir mon résultat <span aria-hidden="true">→</span>' : 'Continuer <span aria-hidden="true">→</span>';
      clearError();
      save();
      if (focus) focusCurrentStep();
    };

    const advance = () => {
      if (!validateStep()) return;
      const from = state.step;
      if (state.step === 1) populateModels();
      if (state.step === 3) prepareFinal();
      else if (state.step === 4 && finalMode === 'decision') showResult();
      else state.step = Math.min(4, state.step + 1);
      track('compatibility_step', { from: from + 1, to: state.step + 1, product: state.product });
      render();
    };

    manualModelToggle.addEventListener('click', () => {
      state.modelMode = 'manual';
      state.manualModel = '';
      state.model = '';
      state.lookupKey = '';
      state.bodyType = '';
      syncModelMode();
      form.elements.manual_model.focus();
      save();
    });

    modelListToggle.addEventListener('click', () => {
      state.modelMode = 'catalogue';
      state.manualModel = '';
      state.model = '';
      syncModelMode();
      form.elements.model.focus();
      save();
    });

    form.addEventListener('submit', (event) => event.preventDefault());
    form.addEventListener('input', (event) => {
      const field = event.target;
      if (!field.name) return;
      if (!started) {
        track('compatibility_start', { source: 'compatibility_page' });
        started = true;
      }
      if (field.name === 'product') {
        state.product = field.value;
        state.hatch = '';
      } else if (field.name === 'brand') {
        state.brand = field.value;
        state.model = '';
        state.manualModel = '';
        state.lookupKey = '';
        state.bodyType = '';
        state.modelMode = 'catalogue';
        form.elements.model.value = '';
        syncModelMode();
        populateModels();
      } else if (field.name === 'model') {
        state.model = field.value;
        state.lookupKey = '';
        state.bodyType = '';
      } else if (field.name === 'manual_model') {
        state.manualModel = field.value;
      } else if (field.name in state) {
        state[field.name] = field.value;
      }
      clearError();
      save();
    });

    form.elements.brand.addEventListener('change', () => {
      const entry = canonicalBrandEntry(form.elements.brand.value);
      if (entry) {
        state.brand = entry.brand;
        form.elements.brand.value = entry.brand;
      }
      populateModels();
      save();
    });

    form.elements.model.addEventListener('change', () => {
      const entry = canonicalModelEntry(form.elements.model.value);
      if (entry) {
        state.model = entry.name;
        state.lookupKey = entry.lookup_key;
        state.bodyType = entry.body_type || '';
        form.elements.model.value = entry.name;
      }
      save();
    });

    form.elements.year.addEventListener('change', () => {
      state.year = form.elements.year.value;
      save();
    });

    nextButton.addEventListener('click', advance);
    backButton.addEventListener('click', () => {
      const from = state.step;
      state.step = Math.max(0, state.step - 1);
      finalMode = '';
      resultTarget.hidden = true;
      decisionScreen.hidden = true;
      track('compatibility_step', { from: from + 1, to: state.step + 1, direction: 'back', product: state.product });
      render();
    });

    const loadData = async () => {
      try {
        const response = await fetch(checker.dataset.sourceUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('data unavailable');
        const data = await response.json();
        catalogue = Array.isArray(data.catalogue) ? data.catalogue : [];
        proofs = Array.isArray(data.proofs) ? data.proofs : [];
        dataReady = data.version === 3 && catalogue.length > 0 && proofs.length > 0;
      } catch (_error) {
        catalogue = [];
        proofs = [];
        dataReady = false;
      }
      syncForm();
      if (state.step === 4) prepareFinal();
      render(false);
    };

    syncForm();
    render(false);
    loadData();
  });
})();
